// Raw design tokens shared by Tailwind and MUI. Do NOT inline hex values — import from here.

// Single source of truth for the Atica brand palette.
export const BRAND = {
  blue: "#004A98",
  blueDark: "#1C4587",
  blueLight: "#E8EEF7",
  blueMedium: "#C5D5EB",
} as const;

export const RADIUS = {
  xs: "4px",
  sm: "6px",
  md: "8px",
  lg: "12px",
  xl: "16px",
  pill: "999px",
} as const;

export const SHADOW = {
  card: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
  cardHover: "0 4px 12px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)",
  stickyBar: "0 -1px 4px rgba(0,0,0,0.06)",
  dialog: "0 8px 30px rgba(0,0,0,0.12)",
  subtle: "0 1px 2px rgba(0,0,0,0.04)",
} as const;

export const PAGE_PADDING = {
  x: "24px",
  y: "20px",
} as const;

export const TRANSITION = {
  fast: "all 0.15s ease",
  normal: "all 0.2s ease",
  slow: "all 0.3s ease",
} as const;

export const COLORS = {
  border: {
    light: "#eef0f2",
    default: "#e2e6ea",
    dark: "#d0d4d8",
  },
  background: {
    page: "#F7F9FC",
    surface: "#FFFFFF",
    surfaceHover: "#F8FAFB",
    muted: "#F1F3F5",
    tableHeader: "#F5F6FA",
  },
  scrollbar: {
    track: "#F1F5F9",
    thumb: "#CBD5E1",
    thumbHover: "#94A3B8",
  },
  text: {
    heading: "#0F172A",
    primary: "#1E293B",
    secondary: "#475569",
    tertiary: "#64748B",
    muted: "#94A3B8",
  },
  status: {
    success: { bg: "#E8F5E9", text: "#2E7D32", border: "#A5D6A7" },
    warning: { bg: "#FFF8E1", text: "#F57F17", border: "#FFE082" },
    error: {
      bg: "#FFEBEE",
      text: "#C62828",
      border: "#FFCDD2",
      surface: "#FFF5F5",
    },
    info: { bg: "#EDE7F6", text: "#4527A0" },
    warningAccent: "#E65100",
    warningMuted: "#795548",
  },
  primary: BRAND.blue,
  primaryHover: BRAND.blueDark,
  primaryLight: BRAND.blueLight,
  primaryMedium: BRAND.blueMedium,
} as const;

// MUI-shaped layer: sx-prop objects, MUI consumers only.
export const BUTTON = {
  contained: {
    backgroundColor: COLORS.primary,
    borderColor: COLORS.primary,
    borderRadius: RADIUS.md,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.8125rem",
    textShadow: "none",
    px: 2,
    py: 0.75,
    boxShadow: "0 1px 2px rgba(48,63,159,0.2)",
    "&:hover": {
      backgroundColor: COLORS.primaryHover,
      borderColor: COLORS.primary,
      boxShadow: "0 2px 4px rgba(48,63,159,0.25)",
    },
  },
  outlined: {
    color: COLORS.primary,
    borderColor: COLORS.border.dark,
    borderRadius: RADIUS.md,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.8125rem",
    px: 2,
    py: 0.75,
    backgroundColor: "#fff",
    "&:hover": {
      backgroundColor: COLORS.primaryLight,
      borderColor: COLORS.primary,
    },
  },
  error: {
    color: "#C62828",
    borderColor: "#FFCDD2",
    borderRadius: RADIUS.md,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.8125rem",
    px: 2,
    py: 0.75,
    backgroundColor: "#fff",
    "&:hover": {
      backgroundColor: "#FFEBEE",
      borderColor: "#EF9A9A",
      color: "#B71C1C",
    },
  },
} as const;

export const CHIP = {
  outlined: {
    color: COLORS.primary,
    borderColor: COLORS.border.dark,
    fontSize: "0.8125rem",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: COLORS.primaryLight,
      borderColor: COLORS.primary,
    },
  },
  deletable: {
    color: COLORS.primary,
    borderColor: COLORS.border.dark,
    fontSize: "0.8125rem",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: COLORS.primaryLight,
      borderColor: COLORS.primary,
    },
    "& .MuiChip-deleteIcon": {
      color: "#94A3B8",
      fontSize: "18px",
      "&:hover": {
        color: "#C62828",
      },
    },
  },
} as const;

export const LINK = {
  action: {
    color: COLORS.primary,
    fontSize: "0.8125rem",
    fontWeight: 500,
    textDecoration: "none",
    transition: TRANSITION.fast,
    "&:hover": {
      textDecoration: "underline",
      color: COLORS.primaryHover,
    },
  },
} as const;

export const BADGE = {
  error: {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.75,
    px: 1.75,
    py: 0.5,
    borderRadius: RADIUS.pill,
    backgroundColor: "#FFEBEE",
    color: "#C62828",
    fontWeight: 600,
    border: "1px solid #FFCDD2",
    transition: TRANSITION.fast,
    boxShadow: SHADOW.subtle,
  },
} as const;

export const ZINDEX = {
  // Matches MUI's legacy appBar z-index (1100) so the Tailwind-rebuilt Navbar keeps its old stacking order.
  shellHeader: 1100,
  // Above MUI's default Dialog/Drawer z-index (1300) — for a sheet nested inside one.
  aboveDialog: 1400,
  popper: 1500,
  calendar: 1600,
  // Radix portals render to document.body, so these sit above every MUI default (max 1500) for the hybrid period.
  radixOverlay: 1650, // Dialog/Sheet backdrop
  radixContent: 1660, // Dialog/Sheet panel content (sits above its own overlay)
  // A Dialog opened from inside an already-open Sheet (e.g. a bounded drawer)
  // needs its own tier above radixContent — otherwise both share radixOverlay/
  // radixContent, and the outer Sheet's content (radixContent) ties with or
  // beats the inner Dialog's overlay (radixOverlay), so the overlay dims
  // everything behind the Sheet but the Sheet itself stays undimmed. Opt in
  // via DialogContent's `nested` prop.
  radixNestedOverlay: 1665,
  radixNestedContent: 1668, // still below radixPopover/radixTooltip, so a select/tooltip inside a nested dialog still renders above it
  radixPopover: 1670,
  radixTooltip: 1680,
  snackbar: 1690, // above every Radix tier, so a toast is never occluded by an open dialog/sheet
} as const;

// Promoted to the raw layer so Radix+Tailwind components can use these without reading MUI's theme context.
// Theme switching (src/theme/variants.ts) is dead code — this is effectively the only variant.
export const SIDEBAR = {
  background: BRAND.blue,
  color: "#FFFFFF",
  badge: {
    background: "#FFFFFF",
    color: "#000000",
  },
} as const;

export const FOOTER = {
  background: "#FFFFFF",
  color: "#9e9e9e",
} as const;

// Same rationale as SIDEBAR/FOOTER above — the indigo variant never overrides
// header.color/background (only header.indicator.background), so the live
// values are always defaultVariant's MUI grey[500]/"#FFF". Promoted here so
// Navbar.tsx doesn't need emotion's useTheme() to read them.
export const HEADER = {
  background: "#FFFFFF",
  color: "#9e9e9e",
} as const;

export const ALERT = {
  error: {
    borderRadius: RADIUS.md,
    border: `1px solid ${COLORS.border.light}`,
  },
} as const;

export interface StatusBadgeStyle {
  bg: string;
  text: string;
  label: string;
}

export const STATUS_BADGE: Record<string, StatusBadgeStyle> = {
  initiated_draft: { bg: "#E8EAF6", text: "#283593", label: "Initiated Draft" },
  draft: { bg: "#E8EAF6", text: "#283593", label: "Draft" },
  unapproved: { bg: "#FFF8E1", text: "#F57F17", label: "Unapproved" },
  pending_approval: {
    bg: "#FFF8E1",
    text: "#F57F17",
    label: "Pending Approval",
  },
  approved: { bg: "#E0F2F1", text: "#00695C", label: "Approved" },
  audit_pending: { bg: "#E3F2FD", text: "#1565C0", label: "Audit Pending" },
  success: { bg: "#E8F5E9", text: "#2E7D32", label: "Success" },
  completed: { bg: "#E8F5E9", text: "#2E7D32", label: "Success" },
  abandoned: { bg: "#FFEBEE", text: "#C62828", label: "Abandoned" },
  in_progress: { bg: "#EDE7F6", text: "#4527A0", label: "In Progress" },
  failed: { bg: "#FFEBEE", text: "#C62828", label: "Failed" },
  error: { bg: "#FFEBEE", text: "#C62828", label: "Failed" },
  default: { bg: "#F5F5F5", text: "#616161", label: "Unknown" },
} as const;

export const LOG_ACTION_COLORS: Record<string, string> = {
  create: "#2E7D32",
  approval: "#1565C0",
  state_change: "#5C6BC0",
  system: "#78909C",
  pricing: "#6A1B9A",
  error: "#C62828",
  default: "#455A64",
} as const;

export const getLogActionColor = (action: string): string => {
  const a = action.toLowerCase();
  if (a.includes("create") || a.includes("created"))
    return LOG_ACTION_COLORS.create;
  if (a.includes("approv") || a.includes("sent for"))
    return LOG_ACTION_COLORS.approval;
  if (a.includes("state") || a.includes("changed"))
    return LOG_ACTION_COLORS.state_change;
  if (a.includes("price") || a.includes("priced") || a.includes("audit"))
    return LOG_ACTION_COLORS.pricing;
  if (a.includes("fail") || a.includes("error") || a.includes("abandon"))
    return LOG_ACTION_COLORS.error;
  if (a.includes("system") || a.includes("auto"))
    return LOG_ACTION_COLORS.system;
  return LOG_ACTION_COLORS.default;
};

const USER_AVATAR_PALETTE = [
  "#3949AB", // Indigo 600
  "#00897B", // Teal 600
  "#5E35B1", // Deep Purple 600
  "#1E88E5", // Blue 600
  "#43A047", // Green 600
  "#E53935", // Red 600
  "#8E24AA", // Purple 600
  "#FB8C00", // Orange 600
  "#00ACC1", // Cyan 600
  "#6D4C41", // Brown 600
] as const;

export const getUserAvatarColor = (identifier: string): string => {
  if (!identifier) return USER_AVATAR_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < identifier.length; i++) {
    hash = identifier.charCodeAt(i) + ((hash << 5) - hash);
  }
  return USER_AVATAR_PALETTE[Math.abs(hash) % USER_AVATAR_PALETTE.length];
};

export const CONTAINER = {
  page: {
    borderRadius: RADIUS.lg,
    boxShadow: SHADOW.card,
    border: `1px solid ${COLORS.border.light}`,
    backgroundColor: COLORS.background.surface,
    overflow: "hidden",
  },
  stickyBar: {
    borderTop: `1px solid ${COLORS.border.default}`,
    backgroundColor: COLORS.background.surface,
    boxShadow: SHADOW.stickyBar,
  },
} as const;

export const DIALOG = {
  paper: {
    borderRadius: RADIUS.lg,
    overflow: "hidden",
  },
  title: {
    px: 3,
    pt: 3,
    pb: 1.5,
    fontSize: "1.125rem",
    fontWeight: 600,
  },
  content: {
    px: 3,
    pt: 1.5,
    pb: 3,
  },
  actions: {
    px: 3,
    pt: 0.5,
    pb: 2,
    gap: 1,
  },
  cancelButton: {
    ...BUTTON.outlined,
    border: "none",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: COLORS.background.muted,
      border: "none",
    },
  },
} as const;

export const PAGINATION = {
  footerContainer: {
    borderTop: `1px solid ${COLORS.border.default}`,
    backgroundColor: "#FAFBFC",
    minHeight: "44px",
    maxHeight: "44px",
  },
  root: {
    color: "#64748B",
    fontSize: "0.75rem",
  },
  toolbar: {
    minHeight: "44px",
  },
  selectLabel: {
    fontSize: "0.75rem",
    fontWeight: 500,
    color: "#64748B",
  },
  displayedRows: {
    fontSize: "0.75rem",
    fontWeight: 500,
    color: "#475569",
  },
  select: {
    fontSize: "0.75rem",
    fontWeight: 500,
    borderRadius: RADIUS.xs,
    border: `1px solid ${COLORS.border.default}`,
    backgroundColor: "#fff",
    py: "1px",
    "&:focus": {
      borderRadius: RADIUS.xs,
      backgroundColor: "#fff",
    },
  },
  actions: {
    gap: "2px",
    "& .MuiIconButton-root": {
      borderRadius: RADIUS.sm,
      border: "none",
      backgroundColor: "transparent",
      width: 28,
      height: 28,
      padding: 0,
      transition: TRANSITION.fast,
      color: "#64748B",
      "&:hover": {
        backgroundColor: COLORS.primaryLight,
        color: COLORS.primary,
      },
      "&.Mui-disabled": {
        backgroundColor: "transparent",
        color: "#CBD5E1",
      },
    },
  },
  scrollbar: {
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: COLORS.scrollbar.track,
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: COLORS.scrollbar.thumb,
      borderRadius: "4px",
      "&:hover": {
        backgroundColor: COLORS.scrollbar.thumbHover,
      },
    },
  },
} as const;
